import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { useAuth } from "providers/auth";
import useFeatureFlags from "apps/user/learner/hooks/use-feature-flags";
import { useTranslation } from "lib/i18n/use-translation";
import { CREATE_ONBOARDING } from "utility/graphQl/mutation";
import { trackEvent } from "utility/mixpanel";
import { logAnalyticsEvent } from "utility/FirebaseAnalytics";
import { logError } from "utility/Function";
import appInfo from "utility/app-info";
import { getOnboardingData, clearOnboardingData } from "./use-onboarding-state";
import { OnboardingLoader } from "./onboarding-loader";
import Logo from "Assets/images/logo.svg";
import AssessmentCardImage from "Assets/images/Index.jpg";
import OriCardImage from "Assets/images/Ori.jpg";
import CloseIcon from "Assets/icon/icon_close.svg";
import UserBlockedIcon from "Assets/icon/icon-user-blocked.svg";
import styles from "../ori-youre-all-set.module.scss";

const ChevronIconRight: React.FC<{ color?: string }> = ({ color = "#fff" }) => (
  <svg
    width="10"
    height="18"
    viewBox="0 0 6 11"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0.21967 0.21967C0.512563 -0.0732233 0.987437 -0.0732233 1.28033 0.21967L5.78033 4.71967C6.07322 5.01256 6.07322 5.48744 5.78033 5.78033L1.28033 10.2803C0.987437 10.5732 0.512563 10.5732 0.21967 10.2803C-0.0732233 9.98744 -0.0732233 9.51256 0.21967 9.21967L4.18934 5.25L0.21967 1.28033C-0.0732233 0.987437 -0.0732233 0.512563 0.21967 0.21967Z"
      fill={color}
    />
  </svg>
);

export const OnboardingSuccess = () => {
  const { learner, refetchUserProfiles } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(["onboard", "common"]);
  const shouldCompleteOnboarding = useRef(true);

  const parsedData = getOnboardingData();
  const alreadyCompleted = Boolean(
    localStorage.getItem("shouldViewReadyScreen"),
  );

  const [completeOnboarding, { data, error }] = useMutation(CREATE_ONBOARDING, {
    variables: {
      input: {
        firstName: parsedData?.firstName,
        lastName: parsedData?.lastName,
        gender: parsedData?.gender,
        birthday: parsedData?.birthday,
        woneDays: parsedData?.woneDays,
        woneDayTimings: parsedData?.woneDayTimings,
        stressFrequency: parsedData?.stressFrequency,
        calendarPermission: parsedData?.calendarPermission,
        // Web has no notifications step to ask through, so it records "no
        // answer" rather than claiming consent it never obtained.
        notificationsEnabled: null,
      },
      appInfo,
    },
  });

  useEffect(() => {
    if (alreadyCompleted || !shouldCompleteOnboarding.current || !parsedData?.firstName) return;
    shouldCompleteOnboarding.current = false;

    trackEvent("Attempting to Complete Onboarding");
    completeOnboarding()
      .then(() => {
        localStorage.setItem("shouldViewReadyScreen", JSON.stringify(true));
        refetchUserProfiles();
        trackEvent("Successfully Completed Onboarding");
      })
      .catch((err) => {
        trackEvent("Failed to Complete Onboarding", {
          Error: err,
          Message: err.message,
        });
        logError(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const firstName =
    data?.learnerOnboarding?.learner?.firstName ?? undefined;

  const [assessmentClicked, setAssessmentClicked] = useState(false);
  const [showOriDisabledToast, setShowOriDisabledToast] = useState(false);
  const { isEnabled } = useFeatureFlags();
  const oriEnabled = isEnabled("ori");

  useEffect(() => {
    trackEvent("Onboarding You're All Set Viewed", {
      "Ori Enabled": oriEnabled,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!alreadyCompleted && parsedData?.firstName === undefined) {
    return <Navigate to="/user/onboard" />;
  }

  if (error) {
    return (
      <Navigate
        to="/user/onboard/error"
        state={{
          params: {
            title: t("onboard:success.error.title"),
            subTitle: t("common:error.generic.title"),
            message: t("onboard:success.error.message"),
            buttonAction: "onboarding",
            buttonText: t("common:action.tryAgain"),
          },
        }}
      />
    );
  }

  if (!alreadyCompleted && !data) {
    return (
      <div className={styles["screen"]} style={{ alignItems: "center", justifyContent: "center" }}>
        <OnboardingLoader />
      </div>
    );
  }

  const handleTakeAssessment = async () => {
    if (assessmentClicked) return;
    setAssessmentClicked(true);
    trackEvent("Onboarding Start Assessment Button Clicked", {
      Destination: "External Assessment",
    });
    await logAnalyticsEvent("start_assessment_after_onboarding", {});
    localStorage.removeItem("shouldViewReadyScreen");
    clearOnboardingData();
    const win = window.open(
      learner.screens.index.coach.assessmentButton.url,
      "_self",
    );
    if (win) {
      win.focus();
    }
  };

  const handleOriCardClick = () => {
    if (!oriEnabled) {
      trackEvent("Onboarding Chat With Ori Card Clicked", {
        Destination: "Disabled Toast",
      });
      trackEvent("Onboarding Chat With Ori Disabled Toast Shown");
      setShowOriDisabledToast(true);
      return;
    }
    trackEvent("Onboarding Chat With Ori Card Clicked", {
      Destination: "Chat",
    });
    localStorage.removeItem("shouldViewReadyScreen");
    clearOnboardingData();
    navigate("/user/chat");
  };

  const handleDismissOriToast = () => {
    trackEvent("Onboarding Chat With Ori Disabled Toast Dismissed");
    setShowOriDisabledToast(false);
  };

  const heading = firstName
    ? t("onboard:success.headingNamed", { name: firstName })
    : t("onboard:success.heading");

  return (
    <div
      className={styles["screen"]}
      data-testid="onboarding-youre-all-set"
    >
      <div className={styles["header"]}>
        <div className={styles["logoWrapper"]}>
          <img src={Logo} alt="Wone" className={styles["logo"]} />
        </div>
      </div>

      <div className={styles["content"]}>
        <svg
          className={styles["doneIcon"]}
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="11" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M7.5 12.5L10.5 15.5L16.5 9"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <h1 className={styles["heading"]}>{heading}</h1>
        <p className={styles["subheading"]}>
          {t("onboard:success.subheading")}
        </p>

        <div className={styles["cards"]}>
          <button
            type="button"
            className={styles["card"]}
            onClick={handleTakeAssessment}
            disabled={assessmentClicked}
            data-testid="onboarding-youre-all-set-assessment"
          >
            <img
              src={AssessmentCardImage}
              alt=""
              className={styles["cardBackground"]}
            />
            <p className={styles["cardTitle"]}>
              {t("onboard:success.assessmentCard")}
            </p>
            <span className={styles["cardChevron"]}>
              <ChevronIconRight />
            </span>
          </button>

          <button
            type="button"
            className={styles["card"]}
            onClick={handleOriCardClick}
            data-testid="onboarding-youre-all-set-ori"
          >
            <img
              src={OriCardImage}
              alt=""
              className={styles["cardBackground"]}
            />
            <p className={styles["cardTitle"]}>
              {t("onboard:success.oriCard")}
            </p>
            {oriEnabled && (
              <span className={styles["cardChevron"]}>
                <ChevronIconRight />
              </span>
            )}
            {!oriEnabled && (
              <div
                className={styles["lockedBanner"]}
                data-testid="onboarding-youre-all-set-ori-locked"
              >
                <img
                  src={UserBlockedIcon}
                  alt=""
                  width={20}
                  height={20}
                  className={styles["lockedIcon"]}
                />
                <span className={styles["lockedText"]}>
                  {t("onboard:success.oriLocked")}
                </span>
              </div>
            )}
          </button>
        </div>
      </div>

      {showOriDisabledToast && (
        <div
          className={styles["toast"]}
          role="status"
          data-testid="onboarding-youre-all-set-ori-toast"
        >
          <span className={styles["toastText"]}>
            {t("onboard:success.oriLocked")}
          </span>
          <button
            type="button"
            className={styles["toastClose"]}
            onClick={handleDismissOriToast}
            aria-label="Dismiss"
          >
            <img src={CloseIcon} alt="" width={16} height={16} />
          </button>
        </div>
      )}
    </div>
  );
};
